import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FilterListIcon from '@mui/icons-material/FilterList'
import {
  useGetReleaseNotesQuery,
  useMarkReleaseNotesSeenMutation,
} from '../../redux/commonRTK'
import PageTitle from '../../common/PageTitle'
import withLayout from '../../hocs/Layout'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {
  NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE,
  SUCCESS, WARNING, ERROR, INFO, SUCCESS_BG, WARNING_BG, ERROR_BG, INFO_BG,
} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'
import {pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx} from '../../styles/patterns'

const sxHeroIcon = {
  width: 52,
  height: 52,
  borderRadius: '12px',
  backgroundColor: 'rgba(255,255,255,0.16)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const sxHeroChip = {
  backgroundColor: 'rgba(255,255,255,0.18)',
  color: WHITE,
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
}

const sxFilterCard = {
  p: 2.5,
  mb: 2.5,
  borderRadius: '10px',
  backgroundColor: WHITE,
  border: `1px solid ${BORDER}`,
  boxShadow: SHADOW.card,
}

const sxNoteCard = {
  borderRadius: '10px',
  border: `1px solid ${BORDER}`,
  backgroundColor: WHITE,
  boxShadow: SHADOW.card,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: PRIMARY_BLUE,
    boxShadow: SHADOW.cardHover,
  },
}

const TYPE_COLOR = {
  feature: {bg: INFO_BG, fg: INFO},
  fix: {bg: WARNING_BG, fg: WARNING},
  improvement: {bg: SUCCESS_BG, fg: SUCCESS},
  breaking: {bg: ERROR_BG, fg: ERROR},
}

const formatDate = (val) => {
  if (!val) return ''
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'})
}

const getChangeType = (note) =>
  (note?.changeType || note?.type || note?.changes?.[0]?.type || '').toString()

const normalizeType = (t) => (t || '').toLowerCase()

const getTypeChip = (type) => {
  const key = normalizeType(type)
  const color = TYPE_COLOR[key] || {bg: INFO_BG, fg: PRIMARY_BLUE}
  return (
    <Chip
      label={type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Update'}
      size='small'
      sx={{
        fontFamily: FONT,
        fontWeight: WEIGHT.semibold,
        fontSize: SIZE['2xs'],
        height: 22,
        backgroundColor: color.bg,
        color: color.fg,
        '& .MuiChip-label': {px: 1.2},
      }}
    />
  )
}

function ReleaseNotes() {
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')

  // RTK Query — cached + deduped.
  const {data, isFetching, error: queryError} = useGetReleaseNotesQuery({limit: 50})
  const [markSeen] = useMarkReleaseNotesSeenMutation()

  const notes = data?.items || []
  const loading = isFetching && notes.length === 0
  const error = queryError
    ? queryError?.data?.message || 'Failed to load release notes'
    : null

  // Mark as seen once — invalidates the unread-count tag.
  useEffect(() => {
    markSeen()
  }, [markSeen])

  const availableTypes = useMemo(() => {
    const set = new Set()
    notes.forEach((n) => {
      const t = getChangeType(n)
      if (t) set.add(t.toString())
    })
    return Array.from(set)
  }, [notes])

  const filtered = useMemo(() => {
    return notes.filter((n) => {
      if (search) {
        const q = search.toLowerCase()
        const t = (n.title || n.headline || '').toLowerCase()
        const s = (n.summary || n.description || '').toLowerCase()
        const v = (n.version || n._id || n.id || '').toString().toLowerCase()
        if (!t.includes(q) && !s.includes(q) && !v.includes(q)) return false
      }
      if (typeFilter !== 'ALL') {
        if (normalizeType(getChangeType(n)) !== normalizeType(typeFilter)) return false
      }
      if (dateFilter) {
        const d = n.releaseDate || n.publishedAt || n.createdAt
        if (!d) return false
        const noteDate = new Date(d).toISOString().slice(0, 10)
        if (noteDate !== dateFilter) return false
      }
      return true
    })
  }, [notes, search, typeFilter, dateFilter])

  const featureCount = notes.filter(
    (n) => normalizeType(getChangeType(n)) === 'feature',
  ).length
  const fixCount = notes.filter((n) => normalizeType(getChangeType(n)) === 'fix').length
  const improvementCount = notes.filter(
    (n) => normalizeType(getChangeType(n)) === 'improvement',
  ).length

  const goToDetail = (note) => {
    const id = note._id || note.id || note.shortCode
    navigate(`/whats-new/${encodeURIComponent(id)}`, {state: {note}})
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>What's New</PageTitle>

      <Box sx={pageHeroSx}>
        <Stack direction='row' spacing={2.5} alignItems='center' sx={{position: 'relative', zIndex: 1}}>
          <Box sx={sxHeroIcon}>
            <CampaignOutlinedIcon sx={{fontSize: 28, color: WHITE}} />
          </Box>
          <Box>
            <Typography sx={heroTitleSx}>What's New in ProShip</Typography>
            <Typography sx={heroSubSx}>
              {notes.length} {notes.length === 1 ? 'release' : 'releases'} — features, fixes &amp; improvements
            </Typography>
            <Stack direction='row' spacing={1} sx={{mt: 1.5, flexWrap: 'wrap', rowGap: 1}}>
              {featureCount > 0 && (
                <Chip
                  label={`${featureCount} ${featureCount === 1 ? 'Feature' : 'Features'}`}
                  size='small'
                  sx={sxHeroChip}
                />
              )}
              {fixCount > 0 && (
                <Chip
                  label={`${fixCount} ${fixCount === 1 ? 'Fix' : 'Fixes'}`}
                  size='small'
                  sx={sxHeroChip}
                />
              )}
              {improvementCount > 0 && (
                <Chip
                  label={`${improvementCount} ${improvementCount === 1 ? 'Improvement' : 'Improvements'}`}
                  size='small'
                  sx={sxHeroChip}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Box sx={sxFilterCard}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size='small'
              placeholder='Search by title, summary or version...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size='small'
              type='date'
              label='Date'
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{shrink: true}}
            />
          </Grid>
        </Grid>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{mt: 2, flexWrap: 'wrap', rowGap: 1}}
        >
          <FilterListIcon fontSize='small' sx={{color: 'text.secondary'}} />
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Type
          </Typography>
          <Chip
            label='All'
            size='small'
            clickable
            onClick={() => setTypeFilter('ALL')}
            variant={typeFilter === 'ALL' ? 'filled' : 'outlined'}
            sx={{
              fontFamily: FONT,
              fontWeight: WEIGHT.semibold,
              ...(typeFilter === 'ALL' && {
                backgroundColor: INFO_BG,
                color: PRIMARY_BLUE,
                border: `1px solid ${PRIMARY_BLUE}`,
              }),
            }}
          />
          {availableTypes.map((t) => {
            const count = notes.filter(
              (n) => normalizeType(getChangeType(n)) === normalizeType(t),
            ).length
            const selected = normalizeType(typeFilter) === normalizeType(t)
            const color = TYPE_COLOR[normalizeType(t)] || {bg: INFO_BG, fg: PRIMARY_BLUE}
            return (
              <Chip
                key={t}
                clickable
                size='small'
                onClick={() => setTypeFilter(t)}
                label={
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: color.fg,
                      }}
                    />
                    {t.charAt(0).toUpperCase() + t.slice(1)} {count}
                  </span>
                }
                variant={selected ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  ...(selected && {
                    backgroundColor: color.bg,
                    color: color.fg,
                    border: `1px solid ${color.fg}`,
                  }),
                }}
              />
            )
          })}
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box sx={{p: 4, textAlign: 'center', color: 'error.main'}}>
          <Typography>{error}</Typography>
        </Box>
      ) : filtered.length === 0 ? (
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: '#fff',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant='body1' color='text.secondary'>
            No release notes found
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filtered.map((note) => {
            const id = note._id || note.id || note.shortCode
            const date = note.releaseDate || note.publishedAt || note.createdAt
            const author = note.publishedBy || note.createdBy || note.author
            const changesCount = Array.isArray(note.changes)
              ? note.changes.length
              : note.changesCount || 1
            const isNew = note.isNew ?? note.unread ?? false
            const title = note.title || note.headline || '(untitled release)'
            const rawSummary = (note.summary || note.description || '').trim()
            // Skip the summary when it just echoes the title — keeps cards tight.
            const summaryText =
              rawSummary && rawSummary.toLowerCase() !== title.toLowerCase() ? rawSummary : ''
            const dotSx = {width: 3, height: 3, borderRadius: '50%', backgroundColor: SUBTLE}
            const metaSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
            return (
              <Card key={id} sx={sxNoteCard} elevation={0}>
                <CardActionArea onClick={() => goToDetail(note)} sx={{p: 2}}>
                  {/* Header — chips left, single date right */}
                  <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75}}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{flexWrap: 'wrap', rowGap: 0.75}}>
                      {getTypeChip(getChangeType(note))}
                      {note.version && (
                        <Chip
                          label={note.version}
                          size='small'
                          variant='outlined'
                          sx={{fontFamily: 'monospace', fontSize: SIZE['2xs'], height: 22}}
                        />
                      )}
                      {note.highlight && (
                        <Chip label='HIGHLIGHT' size='small'
                          sx={{backgroundColor: WARNING_BG, color: WARNING, fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: 10, height: 20}} />
                      )}
                      {isNew && (
                        <Chip label='NEW' size='small'
                          sx={{backgroundColor: INFO_BG, color: PRIMARY_BLUE, fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: 10, height: 20}} />
                      )}
                    </Stack>
                    <Typography sx={{...metaSx, whiteSpace: 'nowrap', flexShrink: 0}}>{formatDate(date)}</Typography>
                  </Box>

                  {/* Title */}
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY}}>
                    {title}
                  </Typography>

                  {/* Summary — only when it adds something beyond the title */}
                  {summaryText && (
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.5}}>
                      {summaryText}
                    </Typography>
                  )}

                  {/* Footer — quiet meta left, view-details link right */}
                  <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1.25, flexWrap: 'wrap', rowGap: 0.5}}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                      {author && <Typography sx={metaSx}>{author}</Typography>}
                      {author && <Box sx={dotSx} />}
                      <Typography sx={metaSx}>
                        {changesCount} {changesCount === 1 ? 'change' : 'changes'}
                      </Typography>
                    </Stack>
                    <Stack direction='row' spacing={0.5} alignItems='center' sx={{color: PRIMARY_BLUE}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}}>View details</Typography>
                      <ArrowForwardIcon sx={{fontSize: 14}} />
                    </Stack>
                  </Box>
                </CardActionArea>
              </Card>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

export default withLayout(ReleaseNotes)
